import {
  FraudDecision,
  IPolicyValidatorResponse,
  NewVerifyDecision,
} from '@src/models/api';
import { ReturnFraudType } from '@src/constants/analytics';
import { ItemToReturn, ReturnsFraudPrompt } from '@src/models/returns';
import { IReturnShipments } from '@src/stores/returnStore';
import { checkFraudType, getFraudMessage, getReturnsFraudPrompts } from '.';
import { TagOnApprissDeniedOrWarned } from './analytics-tagging';

export const processAprissVerifyDecision = (
  hasIneligibleItem: boolean,
  setReturnFraudType: (returnFraudType: ReturnFraudType) => void,
  setRenderModal: (returnsFraudPrompt: ReturnsFraudPrompt) => void,
  closeModal: () => void,
  proceedToNextStep: () => void,
  setFraudDecisions: (fraudDecisions: FraudDecision[]) => void,
  multiVerifyDecision?: NewVerifyDecision,
): ReturnFraudType | undefined => {
  if (!multiVerifyDecision) return undefined;
  const fraudDecisions = multiVerifyDecision.fraudDecisions;
  let fraudType = checkFraudType(multiVerifyDecision);

  if (fraudType) {
    const fraudMessage = getFraudMessage(multiVerifyDecision);
    if (fraudType === 'WARNED' && hasIneligibleItem) {
      fraudType = 'WARNEDINVALID';
    }
    setReturnFraudType(fraudType);
    setRenderModal(
      getReturnsFraudPrompts(fraudMessage, closeModal, proceedToNextStep)?.[
        fraudType
      ],
    );
    TagOnApprissDeniedOrWarned(fraudType);
  }

  // Persist fraudDecisions for finalize call
  setFraudDecisions(fraudDecisions);
  return fraudType;
};

/**
 * Determines if the return contains items with multiple vendor return addresses and updates the following state.
 *
 * - Creates a mapping of each lineItemId to its vendor return address.
 * - Tracks unique addresses to determine if more than one is present.
 * - Migrates existing shipment states (lorpContactDetails, hasAgreed, boxQuantity) to new shipments when applicable
 * - Matches shipments based on lineItems to handle cases where shipments are added/removed
 *
 * @param {HrtvResponseLineItem[]} ssrHrtvRespLineItems - Array of line items from the SSR HRTV response.
 * @param {(isMultiAddressReturn: boolean) => void} setIsMultiAddressReturn
 * @param {(setVendorReturnAddresses: VendorReturnAddresses[]) => void} setVendorReturnAddresses
 * @param {IReturnShipments[]} currentShipments - Current shipments to preserve states from
 */
export const processPolicyValidatorShipments = (
  response: IPolicyValidatorResponse,
  TagPolicyValidatorMultiAddress: () => void,
  currentShipments?: IReturnShipments[],
): IReturnShipments[] => {
  const returnShipments =
    response.returnService.ssrHRTVResponse?.shipments || [];

  // Add shipmentId to each return shipment for easier identification later
  const modifiedReturnShipments = returnShipments.map((shipment, index) => {
    const shipmentId = index + 1;

    // Find matching shipment from current state based on line items
    const existingShipment = currentShipments?.find((currentShipment) => {
      // grab a line item from shipment and see if it exists in currentShipment
      const lineItemKey = shipment.lineItems[0]?.lineItemId;
      return currentShipment.lineItems.some(
        (lineItem) => lineItem.lineItemId === lineItemKey,
      );
    });
    return {
      ...existingShipment,
      shipmentId,
      lineItems: shipment.lineItems,
      shipmentType: shipment.shipmentType,
      shipToAddress: shipment.shipToAddress,
    };
  });

  if (returnShipments.length > 1) {
    TagPolicyValidatorMultiAddress();
  }
  return modifiedReturnShipments;
};

// Utility function to get items from selectedItems that belong to a specific shipment
export const getItemToReturnItemsFromShipment = (
  shipment: IReturnShipments,
  selectedItems: ItemToReturn[],
) =>
  selectedItems.filter((selectedItem) =>
    shipment.lineItems.some(
      (shipmentLineItem) =>
        shipmentLineItem.lineItemId === selectedItem.lineItemId,
    ),
  );
